import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Grid from "@mui/material/Grid";
import { Formik, FieldArray } from "formik";
import * as Yup from "yup";
import { format, isValid, addYears } from "date-fns";
import get from "lodash/get";

import { getROSignatories, getSignatories } from "../../../../../store/selectors/appConfig";
import {
  AutoSaveField as Field,
  SelectAutocomplete,
  DatePicker,
  Input
} from "../../../../../components/Form";
import { DATE_FORMAT, GCC_OR_UAE_NATIONALITY_LIST, UAE } from "../../../../../constants";

import { updateProspect } from "../../../../../store/actions/appConfig";
import { SubmitButton } from "../../../components/SubmitButton/SubmitButton";
import { Divider } from "../../../../../components/Divider";
import {
  ALPHANUMERIC_REGEX,
  TOTAL_EXPERIENCE_YRS_REGEX,
  NUMBER_SLASH_REGEX,
  passportExpiryNotMoreThan10Years,
  passportExpiryGreaterThanIssue
} from "../../../../../utils/validation";
import {
  getTenYearsMaxDateMessage,
  getTenYearsMinDateMessage,
  getGreaterThanMessage,
  getInvalidMessage,
  getMaxDateMessage,
  getPastDateMessage,
  getRequiredMessage
} from "../../../../../utils/getValidationMessage";
import { documentUnique } from "../../../../../constants/documents";
import { RoDocument } from "../../../../UploadDocuments/components/RoDocument/RoDocument";
import { getMinExpiryDate } from "../../../../../utils/helper";
import { FormStyle } from "../styled";
import { ScrollToError } from "components/ScrollToError";
import { AddButton } from "components/Buttons/AddButton";

export const countryOfResidenceSchema = (
  isUAEOrGCC,
  countryOfResidence,
  additionalNationalitiesCount
) =>
  Yup.object().shape({
    visaIssueDate: Yup.date()
      .nullable()
      .test(
        "required",
        getRequiredMessage("Visa issue date"),
        value => isUAEOrGCC || countryOfResidence !== UAE || value
      )
      .min(new Date(1900, 0, 1), getInvalidMessage("Visa issue date"))
      .max(new Date(), getInvalidMessage("Visa issuing date"))
      .typeError(getInvalidMessage("Visa issue date")),
    visaExpiryDate: Yup.date()
      .nullable()
      .test(
        "required",
        getRequiredMessage("Visa expiry date"),
        value => isUAEOrGCC || countryOfResidence !== UAE || value
      )
      .min(new Date(), getInvalidMessage("Visa expiry date"))
      .test(
        "expiry_check",
        "Visa expiry cannot be more than 10 years from Visa issue date",
        function(value) {
          const { visaIssueDate = "" } = this.parent;
          if (!visaIssueDate || !value) return true;
          let visaIssueDateAfterTen = addYears(new Date(visaIssueDate), 10);
          return new Date(value) <= new Date(visaIssueDateAfterTen);
        }
      )
      .typeError(getInvalidMessage("Visa expiry date")),
    visaNumber: Yup.string()
      .nullable()
      .test(
        "required",
        getRequiredMessage("Visa number"),
        value => isUAEOrGCC || countryOfResidence !== UAE || value
      )
      .max(17, "Maximum 17 characters allowed")
      .matches(NUMBER_SLASH_REGEX, getInvalidMessage("Visa number")),
    passportNo: Yup.string()
      .nullable()
      .max(12, "Maximum 12 characters allowed")
      .matches(ALPHANUMERIC_REGEX, getInvalidMessage("Passport number")),
    passportIssueDate: Yup.date()
      .nullable()
      .required(getRequiredMessage("Passport issue date"))
      .min(addYears(new Date(), -10), getTenYearsMinDateMessage("Passport issue date"))
      .max(new Date(), getMaxDateMessage("Passport issuing date"))
      .typeError(getInvalidMessage("Passport issue date")),
    signatoryPassportExpiryDate: Yup.date()
      .nullable()
      .required(getRequiredMessage("Passport expiry date"))
      .min(new Date(), getPastDateMessage("Passport expiry date"))
      .test(
        "not-greater-than-10-years-from-issue",
        getTenYearsMaxDateMessage("Passport expiry date") + " after Passport Issue Date",
        passportExpiryNotMoreThan10Years
      )
      .test(
        "greater-than-issue-date",
        getGreaterThanMessage("Passport Expiry Date") + "Passport Issue Date",
        passportExpiryGreaterThanIssue
      )
      .typeError(getInvalidMessage("Passport expiry date")),
    passportIssuingCountry: Yup.string(),
    oldPassportNo1: Yup.string()
      .nullable()
      .max(12, "Maximum 12 characters allowed")
      .matches(ALPHANUMERIC_REGEX, getInvalidMessage("Old Passport no 1")),
    oldPassportNo2: Yup.string()
      .nullable()
      .max(12, "Maximum 12 characters allowed")
      .matches(ALPHANUMERIC_REGEX, getInvalidMessage("Old Passport no 2")),
    oldPassportNo3: Yup.string()
      .nullable()
      .max(12, "Maximum 12 characters allowed")
      .matches(ALPHANUMERIC_REGEX, getInvalidMessage("Old Passport no 3")),
    background: Yup.string()
      .nullable()
      .max(100, "Maximum 100 characters allowed")
      .matches(TOTAL_EXPERIENCE_YRS_REGEX, getInvalidMessage("Background")),
    additionalPassportDet: Yup.array()
      .of(
        Yup.object().shape({
          passportNo: Yup.string()
            .required(getRequiredMessage("Passport number"))
            .max(12, "Maximum 12 characters allowed")
            .matches(ALPHANUMERIC_REGEX, getInvalidMessage("Passport number")),
          passportIssuingCountry: Yup.string().required(
            getRequiredMessage("Passport issuing country")
          ),
          passportIssueDate: Yup.date()
            .required(getRequiredMessage("Passport issue date"))
            .min(addYears(new Date(), -10), getTenYearsMinDateMessage("Passport issue date"))
            .max(new Date(), getMaxDateMessage("Passport issuing date"))
            .typeError(getInvalidMessage("Passport issue date")),
          signatoryPassportExpiryDate: Yup.date()
            .required(getRequiredMessage("Passport expiry date"))
            .min(new Date(), getPastDateMessage("Passport expiry date"))
            .test(
              "not-greater-than-10-years-from-issue",
              getTenYearsMaxDateMessage("Passport expiry date") + " after Passport Issue Date",
              passportExpiryNotMoreThan10Years
            )
            .test(
              "greater-than-issue-date",
              getGreaterThanMessage("Passport Expiry Date") + "Passport Issue Date",
              passportExpiryGreaterThanIssue
            )
            .typeError(getInvalidMessage("Passport expiry date"))
        })
      )
      .test(
        "all-required-filled",
        `All ${additionalNationalitiesCount} additional passport details must be provided`,
        function(value) {
          if (additionalNationalitiesCount === 0) return true;
          return value && value.length >= additionalNationalitiesCount;
        }
      )
      .max(
        additionalNationalitiesCount,
        `Only ${additionalNationalitiesCount} additional passport details allowed`
      )
  });

export const intialSchema = {
  visaIssueDate: "",
  visaExpiryDate: "",
  visaNumber: "",
  passportNo: "",
  passportIssueDate: "",
  signatoryPassportExpiryDate: "",
  passportIssuingCountry: "",
  oldPassportNo1: "",
  oldPassportNo2: "",
  oldPassportNo3: "",
  background: "",
  additionalPassportDet: []
};

export const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };
export const tomorrow = getMinExpiryDate();

/**
 * Processes passport details array into first and additional nationalities
 * @param {Array} nationality - Array of passport details
 * @returns {Object} Object containing firstNationality and additionalNationalities
 */
export const processNationalityDetails = nationality => {
  if (!Array.isArray(nationality) || nationality.length === 0) {
    return {
      firstNationality: {},
      additionalNationalities: []
    };
  }

  const firstNationality = nationality[0] || {};
  const additionalNationalities = nationality.slice(1) || [];

  return {
    firstNationality,
    additionalNationalities
  };
};

export const CountryOfResidenceStep = ({
  index,
  handleContinue,
  createFormChangeHandler,
  roSignatory,
  nationality,
  countryOfResidence,
  stakeholder,
  updateProspect
}) => {
  const { signatoryId, isSignatory } = stakeholder || {};
  const isUAEOrGCC = nationality
    .flatMap(item => (item.nationality ? [item.nationality] : []))
    .some(ai => GCC_OR_UAE_NATIONALITY_LIST.includes(ai));
  const signatoryPath = `roProspect.signatoryInfo[${index}]`;

  // Memoize processed nationality details
  const { firstNationality, additionalNationalities } = useMemo(
    () => processNationalityDetails(nationality),
    [nationality]
  );

  // Memoize initial values with autofilled additional passport details
  const initialValues = useMemo(() => {
    // Map additional nationalities with existing stored data
    const additionalPassportDet = additionalNationalities.map((nat, index) => {
      const existingEntry = stakeholder?.additionalPassportDet?.[index] || {};
      return {
        passportNo: existingEntry.passportNo || nat.passportNumber || "",
        passportIssuingCountry: existingEntry.passportIssuingCountry || nat.nationality || "",
        passportIssueDate: existingEntry.passportIssueDate || "",
        signatoryPassportExpiryDate: existingEntry.signatoryPassportExpiryDate || ""
      };
    });

    return {
      ...intialSchema,
      passportNo: firstNationality.passportNumber || "",
      passportIssuingCountry: firstNationality.nationality || "",
      passportIssueDate: roSignatory?.passportIssueDate || "",
      signatoryPassportExpiryDate: roSignatory?.signatoryPassportExpiryDate || "",
      visaNumber: roSignatory?.visaNumber || "",
      visaIssueDate: roSignatory?.visaIssueDate || "",
      visaExpiryDate: roSignatory?.visaExpiryDate || "",
      oldPassportNo1: roSignatory?.oldPassportNo1 || "",
      oldPassportNo2: roSignatory?.oldPassportNo2 || "",
      oldPassportNo3: roSignatory?.oldPassportNo3 || "",
      background: roSignatory?.background || "",
      additionalPassportDet
    };
  }, [firstNationality, additionalNationalities, stakeholder, roSignatory]);

  return (
    <Formik
      onSubmit={handleContinue}
      initialValues={initialValues}
      validationSchema={countryOfResidenceSchema(
        isUAEOrGCC,
        countryOfResidence,
        additionalNationalities.length
      )}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {createFormChangeHandler(({ values }) => {
        return (
          <FormStyle>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={6} xs={12}>
                <Field
                  name="visaNumber"
                  path={`${signatoryPath}.visaNumber`}
                  component={Input}
                  label="Visa Number"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="visaIssueDate"
                  path={`${signatoryPath}.visaIssueDate`}
                  component={DatePicker}
                  maxDate={new Date()}
                  label="Visa Issue date"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disableFuture={false}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="visaExpiryDate"
                  path={`${signatoryPath}.visaExpiryDate`}
                  component={DatePicker}
                  minDate={tomorrow}
                  maxDate={addYears(new Date(values.visaIssueDate), 10)}
                  label="Visa Expiry date"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  contextualHelpText="Please enter up to ten years. For any special visa, notify ops team by updating relevant remarks."
                  changeProspect={changeDateProspectHandler}
                  disableFuture={false}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="passportNo"
                  path={`${signatoryPath}.passportNo`}
                  label="Passport no"
                  component={Input}
                  tabIndex="0"
                  isLoadDefaultValueFromStore={false}
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="passportIssuingCountry"
                  label="Passport issuing Country"
                  path={`${signatoryPath}.passportIssuingCountry`}
                  placeholder="Passport issuing Country"
                  component={SelectAutocomplete}
                  datalistId="country"
                  isLoadDefaultValueFromStore={false}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="passportIssueDate"
                  path={`${signatoryPath}.passportIssueDate`}
                  component={DatePicker}
                  maxDate={new Date()}
                  label="Passport Issue date"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disableFuture={true}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="signatoryPassportExpiryDate"
                  path={`${signatoryPath}.signatoryPassportExpiryDate`}
                  component={DatePicker}
                  minDate={tomorrow}
                  maxDate={new Date(new Date().getFullYear() + 10, 11, 31)}
                  label="Passport Expiry date"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                  changeProspect={changeDateProspectHandler}
                  disableFuture={false}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="oldPassportNo1"
                  path={`${signatoryPath}.oldPassportNo1`}
                  label="Old Passport no 1 (If any)"
                  component={Input}
                  tabIndex="0"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="oldPassportNo2"
                  path={`${signatoryPath}.oldPassportNo2`}
                  label="Old Passport no 2 (If any)"
                  component={Input}
                  tabIndex="0"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Field
                  name="oldPassportNo3"
                  path={`${signatoryPath}.oldPassportNo3`}
                  label="Old Passport no 3 (If any)"
                  component={Input}
                  tabIndex="0"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <Field
                  name="background"
                  path={`${signatoryPath}.background`}
                  label="Background"
                  component={Input}
                  multiline
                  rows="4"
                  tabIndex="0"
                  InputProps={{
                    inputProps: { tabIndex: 0 }
                  }}
                />
              </Grid>
            </Grid>
            {/* Additional Passport Details Section */}
            {additionalNationalities.length > 0 && (
              <>
                <Divider.Dark />
                <FieldArray name="additionalPassportDet">
                  {({ push, remove }) => {
                    const currentCount = values.additionalPassportDet?.length || 0;
                    const maxCount = additionalNationalities.length;
                    const canAddMore = currentCount < maxCount;

                    // Check if current last entry is fully filled
                    const isLastEntryComplete = () => {
                      if (currentCount === 0) return true;
                      const lastEntry = values.additionalPassportDet[currentCount - 1];
                      return (
                        lastEntry?.passportNo &&
                        lastEntry?.passportIssuingCountry &&
                        lastEntry?.passportIssueDate &&
                        lastEntry?.signatoryPassportExpiryDate
                      );
                    };

                    const isAddButtonDisabled = !canAddMore || !isLastEntryComplete();

                    const handleAddPassport = () => {
                      if (canAddMore && isLastEntryComplete()) {
                        const nextNationality = additionalNationalities[currentCount];
                        const newEntry = {
                          passportNo: nextNationality?.passportNumber || "",
                          passportIssuingCountry: nextNationality?.nationality || "",
                          passportIssueDate: "",
                          signatoryPassportExpiryDate: ""
                        };
                        push(newEntry);
                      }
                    };

                    return (
                      <>
                        <h5>Additional Nationalities</h5>
                        <Grid container spacing={3}>
                          {values.additionalPassportDet?.map((passport, natIndex) => (
                            <React.Fragment
                              key={`${passport.passportNo ||
                                "empty"}-${passport.passportIssuingCountry || "empty"}`}
                            >
                              <Grid item sm={6} xs={12}>
                                <Field
                                  name={`additionalPassportDet[${natIndex}].passportNo`}
                                  path={`${signatoryPath}.additionalPassportDet[${natIndex}].passportNo`}
                                  label="Passport Number"
                                  isLoadDefaultValueFromStore={false}
                                  component={Input}
                                  tabIndex="0"
                                  InputProps={{
                                    inputProps: { tabIndex: 0 }
                                  }}
                                />
                              </Grid>
                              <Grid item sm={6} xs={12}>
                                <Field
                                  name={`additionalPassportDet[${natIndex}].passportIssuingCountry`}
                                  label="Passport Issuing Country"
                                  path={`${signatoryPath}.additionalPassportDet[${natIndex}].passportIssuingCountry`}
                                  placeholder="Passport issuing Country"
                                  isLoadDefaultValueFromStore={false}
                                  component={SelectAutocomplete}
                                  datalistId="country"
                                />
                              </Grid>
                              <Grid item sm={6} xs={12}>
                                <Field
                                  name={`additionalPassportDet[${natIndex}].passportIssueDate`}
                                  path={`${signatoryPath}.additionalPassportDet[${natIndex}].passportIssueDate`}
                                  component={DatePicker}
                                  maxDate={new Date()}
                                  label="Passport Issue date"
                                  InputProps={{
                                    inputProps: { tabIndex: 0 }
                                  }}
                                  changeProspect={changeDateProspectHandler}
                                  disableFuture={true}
                                />
                              </Grid>
                              <Grid item sm={6} xs={12}>
                                <Field
                                  name={`additionalPassportDet[${natIndex}].signatoryPassportExpiryDate`}
                                  path={`${signatoryPath}.additionalPassportDet[${natIndex}].signatoryPassportExpiryDate`}
                                  component={DatePicker}
                                  minDate={tomorrow}
                                  maxDate={new Date(new Date().getFullYear() + 10, 11, 31)}
                                  label="Passport Expiry Date"
                                  InputProps={{
                                    inputProps: { tabIndex: 0 }
                                  }}
                                  changeProspect={changeDateProspectHandler}
                                  disableFuture={false}
                                />
                              </Grid>
                              {natIndex < currentCount - 1 && (
                                <Grid item xs={12}>
                                  <Divider.Dark />
                                </Grid>
                              )}
                            </React.Fragment>
                          ))}
                          <Grid item xs={12}>
                            <AddButton
                              variant="contained"
                              color="primary"
                              title="Add additional passport details"
                              onClick={handleAddPassport}
                              disabled={isAddButtonDisabled}
                              style={{ marginTop: "16px" }}
                            >
                              Add Additional Passport Details
                            </AddButton>
                          </Grid>
                        </Grid>
                      </>
                    );
                  }}
                </FieldArray>
              </>
            )}
            <Divider.Dark />
            {signatoryId && isSignatory && (
              <Grid item sm={12} xs={12}>
                <RoDocument
                  uniqueId={documentUnique.SignatoryRecentColorPhotograph}
                  stakeholderIndex={`${index}_${signatoryId}`}
                />
              </Grid>
            )}
            <SubmitButton />
          </FormStyle>
        );
      })}
    </Formik>
  );
};

const mapStateToProps = (state, { index }) => ({
  signatoryDocs: get(getROSignatories(state)[index], "stakeholdersDocuments.documents", []),
  nationality: get(getSignatories(state)[index], "passportDetails", []),
  countryOfResidence: get(getSignatories(state)[index], "residenceCountry", []),
  roSignatory: getROSignatories(state)[index]
});

const mapDispatchToProps = {
  updateProspect
};

export const CountryOfResidence = connect(
  mapStateToProps,
  mapDispatchToProps
)(CountryOfResidenceStep);

CountryOfResidenceStep.propTypes = {
  index: PropTypes.number.isRequired,
  handleContinue: PropTypes.func.isRequired,
  createFormChangeHandler: PropTypes.func.isRequired,
  nationality: PropTypes.array,
  countryOfResidence: PropTypes.string,
  stakeholder: PropTypes.object,
  updateProspect: PropTypes.func.isRequired,
  roSignatory: PropTypes.object
};
